import { useEffect, useRef, useState } from 'react';

import { ImageZoomModal } from './image-zoom-modal';

const ImageLoadManager = (() => {
  let subscribers: (() => void)[] = [];
  let visibleImageCount = 0;
  let visibleLoaded = 0;
  let allVisibleLoaded = false;

  return {
    registerVisible: () => {
      visibleImageCount += 1;
    },
    notifyVisibleLoaded: () => {
      visibleLoaded += 1;
      if (visibleLoaded >= visibleImageCount && !allVisibleLoaded) {
        allVisibleLoaded = true;
        subscribers.forEach((cb) => cb());
      }
    },
    subscribe: (cb: () => void) => {
      subscribers.push(cb);
    },
    unsubscribe: (cb: () => void) => {
      subscribers = subscribers.filter((fn) => fn !== cb);
    },
    isReady: () => allVisibleLoaded,
  };
})();

interface ILazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  allowZoom?: boolean;
}

export const LazyImage = ({ src, alt, allowZoom, ...rest }: ILazyImageProps) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (!imgRef.current) return;

    const node = imgRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            setHasBeenVisible(true);
            ImageLoadManager.registerVisible();
          }
        });
      },
      { rootMargin: '100px' },
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
    };
  }, []);

  useEffect(() => {
    if (hasBeenVisible || ImageLoadManager.isReady()) {
      setShouldLoad(true);

      return;
    }

    const callback = () => setShouldLoad(true);
    ImageLoadManager.subscribe(callback);

    return () => {
      ImageLoadManager.unsubscribe(callback);
    };
  }, [hasBeenVisible]);

  return (
    <div {...rest} className={`${rest.className ?? ''} relative flex items-center justify-center overflow-hidden`}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img
        ref={imgRef}
        src={shouldLoad ? src : undefined}
        alt={alt}
        loading="lazy"
        onLoad={() => {
          setLoaded(true);
          if (inView) {
            ImageLoadManager.notifyVisibleLoaded();
          }
        }}
        className={'absolute top-0 right-0 bottom-0 left-0 object-fit transition-opacity duration-300 w-full h-full'}
      />
      {allowZoom && loaded && (
        <button
          onClick={() => setZoomed(true)}
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(0,0,0,0.25)] shadow cursor-pointer transition-colors hover:bg-[rgba(0,0,0,0.65)]"
        >
          🔍
        </button>
      )}
      {zoomed && <ImageZoomModal src={src} onClose={() => setZoomed(false)} />}
    </div>
  );
};
